// src/pages/AdminSettings.tsx

import React, { useState, useEffect } from "react";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import adminService from "../../services/adminService";
import { formatXRP } from "../../utils/formatters";

import {
  CurrencyDollarIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  GiftIcon,
  ArrowDownIcon,
  MegaphoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalculatorIcon,
  SparklesIcon,
  FireIcon,
  SignalIcon,
} from "@heroicons/react/24/outline";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function roundToClean(value: number): number {
  if (value <= 0) return 0;
  if (value < 1000) return Math.round(value / 10) * 10;
  if (value < 10000) return Math.round(value / 100) * 100;
  return Math.round(value / 1000) * 1000;
}

// ── Tier badge ────────────────────────────────────────────────────────────────
const TierBadge: React.FC<{ tier: "high" | "mid" | "low" }> = ({ tier }) => {
  const styles = {
    high: {
      bg: "bg-yellow-900/40 border-yellow-700 text-yellow-300",
      icon: <FireIcon className="w-3.5 h-3.5" />,
      label: "High",
    },
    mid: {
      bg: "bg-blue-900/40 border-blue-700 text-blue-300",
      icon: <SignalIcon className="w-3.5 h-3.5" />,
      label: "Mid",
    },
    low: {
      bg: "bg-gray-800 border-gray-600 text-gray-400",
      icon: <SparklesIcon className="w-3.5 h-3.5" />,
      label: "Low",
    },
  };
  const s = styles[tier];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${s.bg}`}
    >
      {s.icon}
      {s.label}
    </span>
  );
};

const AdminSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "xrp" | "airdrop" | "withdrawal" | "dashboard"
  >("xrp");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // XRP Address
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [showEditAddress, setShowEditAddress] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<any>(null);
  const [xrpAddresses, setXrpAddresses] = useState<any[]>([]);

  // Airdrop
  const [showAddAirdrop, setShowAddAirdrop] = useState(false);
  const [showEditAirdrop, setShowEditAirdrop] = useState<any>(null);
  const [showDeleteAirdrop, setShowDeleteAirdrop] = useState<any>(null);
  const [showDistributeAirdrop, setShowDistributeAirdrop] = useState<any>(null);
  const [airdrops, setAirdrops] = useState<any[]>([]);

  // Withdrawal settings — 3-tier activity-based
  const [withdrawalSettings, setWithdrawalSettings] = useState({
    allowed_days: [0, 1, 2, 3, 4, 5, 6] as number[],
    tier1_threshold: 500,
    tier1_pct: 80,
    tier2_threshold: 100,
    tier2_pct: 50,
    tier3_pct: 20,
    floor_limit: 20,
    level_bonus_pct: 40,
  });
  const [withdrawalSettingsLoading, setWithdrawalSettingsLoading] =
    useState(false);

  // Calculator
  const [calcEarnings, setCalcEarnings] = useState<string>("");
  const [calcLvlBonus, setCalcLvlBonus] = useState<string>("");

  // Broadcast
  const [broadcast, setBroadcast] = useState<{
    message: string;
    is_active: boolean;
    type: "info" | "warning" | "success" | "danger";
    speed: number;
  }>({ message: "", is_active: false, type: "info", speed: 40 });
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  // XRP Address form
  const [newAddress, setNewAddress] = useState({
    address: "",
    destination_tag: "",
    memo_type: "",
    label: "",
    is_active: false,
    qr_code: null as File | null,
  });

  // Airdrop form
  const [newAirdrop, setNewAirdrop] = useState({
    name: "",
    description: "",
    amount: "",
    distribution_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 16),
    criteria: "",
  });

  useEffect(() => {
    loadXrpAddresses();
    loadAirdrops();
    loadWithdrawalSettings();
    loadBroadcast();
  }, []);

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadBroadcast = async () => {
    try {
      setBroadcastLoading(true);
      const data = await adminService.getBroadcastMessage();
      setBroadcast({
        message: data.message ?? "",
        is_active: data.is_active ?? false,
        type: data.type ?? "info",
        speed: data.speed ?? 40,
      });
    } catch {
    } finally {
      setBroadcastLoading(false);
    }
  };

  const loadXrpAddresses = async () => {
    try {
      setLoading(true);
      setXrpAddresses(await adminService.getXrpAddresses());
    } catch {
      setError("Failed to load XRP addresses");
    } finally {
      setLoading(false);
    }
  };

  const loadAirdrops = async () => {
    try {
      setLoading(true);
      setAirdrops(await adminService.getAirdrops());
    } catch {
      setError("Failed to load airdrops");
    } finally {
      setLoading(false);
    }
  };

  const loadWithdrawalSettings = async () => {
    try {
      setWithdrawalSettingsLoading(true);
      const data = await adminService.getWithdrawalSettings();
      setWithdrawalSettings({
        allowed_days: data.allowed_days ?? [0, 1, 2, 3, 4, 5, 6],
        tier1_threshold: data.tier1_threshold ?? 500,
        tier1_pct: data.tier1_pct ?? 80,
        tier2_threshold: data.tier2_threshold ?? 100,
        tier2_pct: data.tier2_pct ?? 50,
        tier3_pct: data.tier3_pct ?? 20,
        floor_limit: data.floor_limit ?? 20,
        level_bonus_pct: data.level_bonus_pct ?? 40,
      });
    } catch {
      setError("Failed to load withdrawal settings");
    } finally {
      setWithdrawalSettingsLoading(false);
    }
  };

  // ── Broadcast ──────────────────────────────────────────────────────────────

  const handleSaveBroadcast = async () => {
    if (!broadcast.message.trim() && broadcast.is_active) {
      setError("Message cannot be empty when broadcast is active");
      return;
    }
    try {
      setBroadcastLoading(true);
      setError(null);
      await adminService.updateBroadcastMessage(broadcast);
      setSuccess(
        broadcast.is_active
          ? "Broadcast is now LIVE on all dashboards."
          : "Broadcast saved and hidden.",
      );
    } catch (err: any) {
      setError(err.message || "Failed to save broadcast");
    } finally {
      setBroadcastLoading(false);
    }
  };

  // ── XRP Address handlers ───────────────────────────────────────────────────

  const handleAddAddress = async () => {
    if (!newAddress.address.trim() || !validateXrpAddress(newAddress.address)) {
      setError("Valid XRP address is required");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const formData = new FormData();
      formData.append("address", newAddress.address);
      if (newAddress.destination_tag)
        formData.append("destination_tag", newAddress.destination_tag);
      if (newAddress.memo_type)
        formData.append("memo_type", newAddress.memo_type);
      if (newAddress.label) formData.append("label", newAddress.label);
      formData.append("is_active", newAddress.is_active ? "1" : "0");
      if (newAddress.qr_code) formData.append("qr_code", newAddress.qr_code);
      await adminService.createXrpAddress(formData);
      setSuccess("XRP address added successfully");
      setShowAddAddress(false);
      setNewAddress({
        address: "",
        destination_tag: "",
        memo_type: "",
        label: "",
        is_active: false,
        qr_code: null,
      });
      loadXrpAddresses();
    } catch (err: any) {
      setError(err.message || "Failed to add XRP address");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddress = async () => {
    if (!showEditAddress) return;
    try {
      setLoading(true);
      setError(null);
      await adminService.updateXrpAddress(showEditAddress.id, {
        destination_tag: showEditAddress.destination_tag || null,
        memo_type: showEditAddress.memo_type || null,
        label: showEditAddress.label || null,
        is_active: Boolean(showEditAddress.is_active),
      });
      setSuccess("XRP address updated");
      setShowEditAddress(null);
      loadXrpAddresses();
    } catch (err: any) {
      setError(err.message || "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async () => {
    if (!showDeleteConfirm) return;
    try {
      setLoading(true);
      setError(null);
      await adminService.deleteXrpAddress(showDeleteConfirm.id);
      setSuccess("XRP address deleted");
      setShowDeleteConfirm(null);
      loadXrpAddresses();
    } catch (err: any) {
      setError(err.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  // ── Airdrop handlers ───────────────────────────────────────────────────────

  const handleAddAirdrop = async () => {
    if (
      !newAirdrop.name.trim() ||
      !newAirdrop.amount.trim() ||
      !newAirdrop.distribution_date.trim()
    ) {
      setError("Name, amount, and distribution date are required");
      return;
    }
    const dateObj = new Date(newAirdrop.distribution_date);
    if (isNaN(dateObj.getTime())) {
      setError("Please select a valid date");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await adminService.createAirdrop({
        ...newAirdrop,
        distribution_date: dateObj.toISOString().slice(0, 16),
      });
      setSuccess("Airdrop created");
      setShowAddAirdrop(false);
      setNewAirdrop({
        name: "",
        description: "",
        amount: "",
        distribution_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 16),
        criteria: "",
      });
      loadAirdrops();
    } catch (err: any) {
      setError(err.message || "Failed to create airdrop");
    } finally {
      setLoading(false);
    }
  };

  const handleEditAirdrop = async () => {
    if (!showEditAirdrop?.name?.trim()) {
      setError("Airdrop name is required");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await adminService.updateAirdrop(showEditAirdrop.id, {
        name: showEditAirdrop.name,
        description: showEditAirdrop.description,
        amount: showEditAirdrop.amount,
        distribution_date: new Date(showEditAirdrop.distribution_date)
          .toISOString()
          .slice(0, 16),
        criteria: showEditAirdrop.criteria,
      });
      setSuccess("Airdrop updated");
      setShowEditAirdrop(null);
      loadAirdrops();
    } catch (err: any) {
      setError(err.message || "Failed to update airdrop");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAirdrop = async () => {
    if (!showDeleteAirdrop) return;
    try {
      setLoading(true);
      setError(null);
      await adminService.deleteAirdrop(showDeleteAirdrop.id);
      setSuccess("Airdrop deleted");
      setShowDeleteAirdrop(null);
      loadAirdrops();
    } catch (err: any) {
      setError(err.message || "Failed to delete airdrop");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAirdropStatus = async (airdropId: number) => {
    try {
      setLoading(true);
      setError(null);
      await adminService.toggleAirdropStatus(airdropId);
      setSuccess("Airdrop status updated");
      loadAirdrops();
    } catch (err: any) {
      setError(err.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleDistributeAirdrop = async () => {
    if (!showDistributeAirdrop) return;
    try {
      setLoading(true);
      setError(null);
      await adminService.distributeAirdrop(showDistributeAirdrop.id);
      setSuccess("Airdrop distribution started");
      setShowDistributeAirdrop(null);
      loadAirdrops();
    } catch (err: any) {
      setError(err.message || "Failed to distribute");
    } finally {
      setLoading(false);
    }
  };

  // ── Withdrawal settings handlers ───────────────────────────────────────────

  const toggleWithdrawalDay = (dow: number) => {
    setWithdrawalSettings((prev) => {
      const current = prev.allowed_days;
      if (current.includes(dow) && current.length <= 1) {
        setError("At least one withdrawal day must be selected");
        return prev;
      }
      return {
        ...prev,
        allowed_days: current.includes(dow)
          ? current.filter((d) => d !== dow)
          : [...current, dow].sort((a, b) => a - b),
      };
    });
  };

  const handleSaveWithdrawalSettings = async () => {
    if (withdrawalSettings.allowed_days.length === 0) {
      setError("Select at least one withdrawal day");
      return;
    }
    if (
      withdrawalSettings.tier1_threshold <= withdrawalSettings.tier2_threshold
    ) {
      setError(
        "Tier 1 (High) threshold must be greater than Tier 2 (Mid) threshold",
      );
      return;
    }
    if (
      withdrawalSettings.tier1_pct <= 0 ||
      withdrawalSettings.tier2_pct <= 0 ||
      withdrawalSettings.tier3_pct <= 0
    ) {
      setError("All tier percentages must be greater than 0");
      return;
    }
    try {
      setWithdrawalSettingsLoading(true);
      setError(null);
      await adminService.updateWithdrawalSettings(withdrawalSettings);
      setSuccess("Withdrawal settings saved successfully");
      await loadWithdrawalSettings();
    } catch (err: any) {
      setError(err.message || "Failed to save withdrawal settings");
    } finally {
      setWithdrawalSettingsLoading(false);
    }
  };

  // ── Calculator — mirrors backend logic exactly ─────────────────────────────

  function calcWithdrawalLimit(
    weekEarnings: number,
    allTimeLvlBonus: number = 0,
  ): {
    limit: number;
    baseLimit: number;
    bonusTopUp: number;
    tier: "high" | "mid" | "low";
    pct: number;
    label: string;
  } {
    const {
      tier1_threshold,
      tier1_pct,
      tier2_threshold,
      tier2_pct,
      tier3_pct,
      floor_limit,
      level_bonus_pct,
    } = withdrawalSettings;
    let tier: "high" | "mid" | "low";
    let pct: number;
    if (weekEarnings >= tier1_threshold) {
      tier = "high";
      pct = tier1_pct;
    } else if (weekEarnings >= tier2_threshold) {
      tier = "mid";
      pct = tier2_pct;
    } else {
      tier = "low";
      pct = tier3_pct;
    }
    const baseLimit = Math.max(
      floor_limit,
      roundToClean(weekEarnings * (pct / 100)),
    );
    const bonusTopUp = roundToClean(allTimeLvlBonus * (level_bonus_pct / 100));
    const limit = baseLimit + bonusTopUp;
    return {
      limit,
      baseLimit,
      bonusTopUp,
      tier,
      pct,
      label: `${pct}% of ${weekEarnings.toLocaleString()} XRP + ${level_bonus_pct}% of ${allTimeLvlBonus.toLocaleString()} XRP bonus = ${limit.toLocaleString()} XRP/day`,
    };
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  const validateXrpAddress = (address: string) =>
    /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(address);
  const formatDate = (dateString: string) => {
    if (!dateString) return "Never";
    const d = new Date(dateString);
    return (
      d.toLocaleDateString() +
      " " +
      d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };
  const formatXRPAmount = (amount: any) => {
    const num =
      typeof amount === "string" ? parseFloat(amount) : Number(amount ?? 0);
    return (isNaN(num) ? "0.00" : formatXRP(num)) + " XRP";
  };
  const getBadgeClass = (status: string) =>
    ({
      active: globalStyle.badgeSuccess,
      pending: globalStyle.badgeWarning,
      completed: globalStyle.badgePrimary,
    })[status] ?? globalStyle.badgeInfo;

  const renderLoadingState = () => (
    <div className="text-center py-12">
      <svg
        className={`${globalStyle.loadingSpinner} mx-auto`}
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <p className={`${globalStyle.body2} mt-4 text-gray-400`}>Loading...</p>
    </div>
  );

  const calcResult =
    calcEarnings !== "" && parseFloat(calcEarnings) >= 0
      ? calcWithdrawalLimit(
          parseFloat(calcEarnings) || 0,
          parseFloat(calcLvlBonus) || 0,
        )
      : null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={globalStyle.container}>
      <Header />

      <main className="page-content">
        <div className="mb-6">
          <h1 className={globalStyle.h3}>Admin Settings</h1>
          <p className={`${globalStyle.body2} text-gray-400`}>
            Configure platform settings and manage system
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-1 border-b border-gray-800">
            {[
              { id: "xrp", label: "XRP Addresses", icon: CurrencyDollarIcon },
              { id: "airdrop", label: "Airdrops", icon: GiftIcon },
              {
                id: "withdrawal",
                label: "Withdrawal Rules",
                icon: ArrowDownIcon,
              },
              { id: "dashboard", label: "Dashboard", icon: MegaphoneIcon },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-3 font-medium ${globalStyle.transition} flex items-center ${activeTab === tab.id ? "text-primary border-b-2 border-primary" : "text-gray-400 hover:text-gray-300"}`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            onClose={() => setError(null)}
            className="mb-6"
          />
        )}
        {success && (
          <Alert
            type="success"
            message={success}
            onClose={() => setSuccess(null)}
            className="mb-6"
          />
        )}

        {/* ── XRP Addresses ───────────────────────────────────────────────── */}
        {activeTab === "xrp" && (
          <Card padding="lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className={globalStyle.h5}>XRP Address Management</h3>
              <Button onClick={() => setShowAddAddress(true)}>
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Address
              </Button>
            </div>
            {loading && xrpAddresses.length === 0 ? (
              renderLoadingState()
            ) : xrpAddresses.length === 0 ? (
              <div className="text-center py-12">
                <CurrencyDollarIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <h3 className={globalStyle.h5}>No XRP Addresses</h3>
                <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                  Add your first XRP address to start accepting deposits.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {xrpAddresses.map((address) => (
                  <Card key={address.id} className="relative">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="mb-4 md:mb-0">
                        <div className="flex items-center mb-2">
                          <code className="bg-gray-800 px-3 py-1 rounded text-sm font-mono truncate max-w-md text-white">
                            {address.address}
                          </code>
                          {address.is_active && (
                            <span
                              className={`${globalStyle.badge} ${globalStyle.badgeSuccess} ml-2`}
                            >
                              Active
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {address.label && (
                            <span className="text-sm text-gray-400">
                              Label: {address.label}
                            </span>
                          )}
                          {address.destination_tag && (
                            <span className="text-sm text-gray-400">
                              Destination Tag: {address.destination_tag}
                            </span>
                          )}
                          {address.memo_type && (
                            <span className="text-sm text-gray-400">
                              Memo Type: {address.memo_type}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="tertiary"
                          onClick={() => setShowEditAddress(address)}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => setShowDeleteConfirm(address)}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* ── Airdrops ────────────────────────────────────────────────────── */}
        {activeTab === "airdrop" && (
          <Card padding="lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className={globalStyle.h5}>Airdrop Management</h3>
              <Button onClick={() => setShowAddAirdrop(true)}>
                <PlusIcon className="w-4 h-4 mr-2" />
                Create Airdrop
              </Button>
            </div>
            {loading && airdrops.length === 0 ? (
              renderLoadingState()
            ) : airdrops.length === 0 ? (
              <div className="text-center py-12">
                <GiftIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <h3 className={globalStyle.h5}>No Airdrops</h3>
                <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                  Create your first airdrop to reward users.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {airdrops.map((airdrop) => (
                  <Card key={airdrop.id} className="relative">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="mb-4 md:mb-0 md:flex-1">
                        <div className="flex items-center mb-2">
                          <h4 className={`${globalStyle.h6} mr-3`}>
                            {airdrop.name}
                          </h4>
                          <span
                            className={`${globalStyle.badge} ${getBadgeClass(airdrop.status)}`}
                          >
                            {airdrop.status}
                          </span>
                        </div>
                        {airdrop.description && (
                          <p
                            className={`${globalStyle.body2} text-gray-400 mb-2`}
                          >
                            {airdrop.description}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-3">
                          <div>
                            <span className="text-sm font-medium text-gray-500">
                              Amount:
                            </span>
                            <span className="ml-2 text-sm text-white">
                              {formatXRPAmount(airdrop.amount)}
                            </span>
                          </div>
                          {airdrop.distribution_date && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">
                                Distribution Date:
                              </span>
                              <span className="ml-2 text-sm text-white">
                                {formatDate(airdrop.distribution_date)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {airdrop.status === "pending" && (
                          <Button
                            size="sm"
                            onClick={() => setShowDistributeAirdrop(airdrop)}
                          >
                            Distribute Now
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleToggleAirdropStatus(airdrop.id)}
                        >
                          {airdrop.status === "active"
                            ? "Deactivate"
                            : "Activate"}
                        </Button>
                        {airdrop.status !== "completed" && (
                          <Button
                            size="sm"
                            variant="tertiary"
                            onClick={() =>
                              setShowEditAirdrop({
                                ...airdrop,
                                distribution_date: new Date(
                                  airdrop.distribution_date,
                                )
                                  .toISOString()
                                  .slice(0, 16),
                              })
                            }
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                        )}
                        {airdrop.status !== "completed" && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setShowDeleteAirdrop(airdrop)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* ── Withdrawal Rules ─────────────────────────────────────────────── */}
        {activeTab === "withdrawal" && (
          <div className="space-y-6">
            {/* Allowed Days */}
            <Card padding="lg">
              <h3 className={`${globalStyle.h5} mb-2`}>
                Allowed Withdrawal Days
              </h3>
              <p className={`${globalStyle.body2} text-gray-400 mb-6`}>
                Users can only submit withdrawal requests on the selected days.
              </p>
              {withdrawalSettingsLoading ? (
                renderLoadingState()
              ) : (
                <>
                  <div className="grid grid-cols-7 gap-2 mb-4">
                    {DAY_NAMES.map((name, dow) => {
                      const on = withdrawalSettings.allowed_days.includes(dow);
                      return (
                        <button
                          key={dow}
                          onClick={() => toggleWithdrawalDay(dow)}
                          className={`flex flex-col items-center py-3 px-1 rounded-lg border transition-all ${on ? "border-primary bg-primary/10 text-primary" : "border-gray-700 bg-gray-900 text-gray-500 hover:border-gray-600"}`}
                        >
                          <span className="text-xs font-medium">
                            {DAY_ABBR[dow]}
                          </span>
                          <span
                            className={`mt-1 w-2 h-2 rounded-full ${on ? "bg-primary" : "bg-gray-700"}`}
                          />
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-sm text-gray-300">
                    <span className="font-medium text-white">Open: </span>
                    {withdrawalSettings.allowed_days.length === 7
                      ? "Every day"
                      : withdrawalSettings.allowed_days.length === 0
                        ? "None (disabled)"
                        : withdrawalSettings.allowed_days
                            .map((d) => DAY_NAMES[d])
                            .join(", ")}
                  </p>
                </>
              )}
            </Card>

            {/* 3-Tier Activity Configuration */}
            <Card padding="lg">
              <div className="mb-6">
                <h3 className={`${globalStyle.h5} mb-1`}>
                  Activity-Based Withdrawal Tiers
                </h3>
                <p className={`${globalStyle.body2} text-gray-400`}>
                  Each user's daily withdrawal limit is calculated as a{" "}
                  <strong className="text-white">
                    percentage of their total earnings in the last 7 days
                  </strong>{" "}
                  (bot + referral + all types combined). The tier they fall into
                  depends on how much they earned.
                </p>
              </div>

              {/* How it works */}
              <div className="bg-blue-900/20 border border-blue-800/40 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-blue-300 mb-2">
                  How it works
                </h4>
                <ul className="text-sm text-blue-200 space-y-1">
                  <li>
                    · Sum all earnings (bot + referral + all types) in the last
                    7 days
                  </li>
                  <li>
                    · If earnings ≥ Tier 1 threshold →{" "}
                    <span className="text-yellow-300 font-medium">High</span>{" "}
                    tier — gets Tier 1 % of earnings
                  </li>
                  <li>
                    · If earnings ≥ Tier 2 threshold →{" "}
                    <span className="text-blue-300 font-medium">Mid</span> tier
                    — gets Tier 2 % of earnings
                  </li>
                  <li>
                    · Otherwise →{" "}
                    <span className="text-gray-300 font-medium">Low</span> tier
                    — gets Tier 3 % of earnings
                  </li>
                  <li>
                    · The result is rounded to a clean number, then the floor
                    limit is applied as a minimum
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                {/* ── Tier 1: High ── */}
                <div className="rounded-lg border border-yellow-800/40 bg-yellow-900/10 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <TierBadge tier="high" />
                    <span className="text-sm font-medium text-yellow-200">
                      High Activity
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>
                        Minimum 7-day earnings to qualify (XRP)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="10"
                          value={withdrawalSettings.tier1_threshold}
                          onChange={(e) =>
                            setWithdrawalSettings((p) => ({
                              ...p,
                              tier1_threshold: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className={`${globalStyle.input} pr-14`}
                          placeholder="500"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                          XRP
                        </span>
                      </div>
                      <p
                        className={`${globalStyle.caption} mt-1 text-gray-400`}
                      >
                        Must be greater than Tier 2 threshold
                      </p>
                    </div>
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>
                        Withdrawal limit (% of 7-day earnings)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min="1"
                          max="100"
                          step="1"
                          value={withdrawalSettings.tier1_pct}
                          onChange={(e) =>
                            setWithdrawalSettings((p) => ({
                              ...p,
                              tier1_pct: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className={globalStyle.input}
                          placeholder="80"
                        />
                        <span className="text-white font-bold text-lg shrink-0">
                          %
                        </span>
                      </div>
                      {withdrawalSettings.tier1_threshold > 0 &&
                        withdrawalSettings.tier1_pct > 0 && (
                          <p
                            className={`${globalStyle.caption} mt-1 text-yellow-300`}
                          >
                            At threshold:{" "}
                            {roundToClean(
                              (withdrawalSettings.tier1_threshold *
                                withdrawalSettings.tier1_pct) /
                                100,
                            ).toLocaleString()}{" "}
                            XRP/day
                          </p>
                        )}
                    </div>
                  </div>
                </div>

                {/* ── Tier 2: Mid ── */}
                <div className="rounded-lg border border-blue-800/40 bg-blue-900/10 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <TierBadge tier="mid" />
                    <span className="text-sm font-medium text-blue-200">
                      Mid Activity
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>
                        Minimum 7-day earnings to qualify (XRP)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="10"
                          value={withdrawalSettings.tier2_threshold}
                          onChange={(e) =>
                            setWithdrawalSettings((p) => ({
                              ...p,
                              tier2_threshold: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className={`${globalStyle.input} pr-14`}
                          placeholder="100"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                          XRP
                        </span>
                      </div>
                      <p
                        className={`${globalStyle.caption} mt-1 text-gray-400`}
                      >
                        Must be less than Tier 1 threshold
                      </p>
                    </div>
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>
                        Withdrawal limit (% of 7-day earnings)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min="1"
                          max="100"
                          step="1"
                          value={withdrawalSettings.tier2_pct}
                          onChange={(e) =>
                            setWithdrawalSettings((p) => ({
                              ...p,
                              tier2_pct: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className={globalStyle.input}
                          placeholder="50"
                        />
                        <span className="text-white font-bold text-lg shrink-0">
                          %
                        </span>
                      </div>
                      {withdrawalSettings.tier2_threshold > 0 &&
                        withdrawalSettings.tier2_pct > 0 && (
                          <p
                            className={`${globalStyle.caption} mt-1 text-blue-300`}
                          >
                            At threshold:{" "}
                            {roundToClean(
                              (withdrawalSettings.tier2_threshold *
                                withdrawalSettings.tier2_pct) /
                                100,
                            ).toLocaleString()}{" "}
                            XRP/day
                          </p>
                        )}
                    </div>
                  </div>
                </div>

                {/* ── Tier 3: Low ── */}
                <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <TierBadge tier="low" />
                    <span className="text-sm font-medium text-gray-300">
                      Low / No Activity
                    </span>
                    <span className="text-xs text-gray-500 ml-1">
                      (everyone below Tier 2 threshold)
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>
                        Withdrawal limit (% of 7-day earnings)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min="1"
                          max="100"
                          step="1"
                          value={withdrawalSettings.tier3_pct}
                          onChange={(e) =>
                            setWithdrawalSettings((p) => ({
                              ...p,
                              tier3_pct: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className={globalStyle.input}
                          placeholder="20"
                        />
                        <span className="text-white font-bold text-lg shrink-0">
                          %
                        </span>
                      </div>
                      <p
                        className={`${globalStyle.caption} mt-1 text-gray-400`}
                      >
                        Applied to any earnings below Tier 2 threshold
                      </p>
                    </div>
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>
                        Floor limit (XRP)
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="5"
                          value={withdrawalSettings.floor_limit}
                          onChange={(e) =>
                            setWithdrawalSettings((p) => ({
                              ...p,
                              floor_limit: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className={`${globalStyle.input} pr-14`}
                          placeholder="20"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                          XRP
                        </span>
                      </div>
                      <p
                        className={`${globalStyle.caption} mt-1 text-gray-400`}
                      >
                        Minimum limit regardless of earnings — prevents users
                        from being locked out entirely
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Threshold ordering warning */}
              {withdrawalSettings.tier1_threshold <=
                withdrawalSettings.tier2_threshold && (
                <div className="bg-red-900 border border-red-800 rounded-lg p-3 mt-4">
                  <p className="text-sm text-red-200">
                    Tier 1 (High) threshold must be strictly greater than Tier 2
                    (Mid) threshold.
                  </p>
                </div>
              )}
            </Card>

            {/* Level-Up Bonus Top-Up */}
            <Card padding="lg">
              <div className="mb-4">
                <h3 className={`${globalStyle.h5} mb-1`}>
                  Level-Up Bonus Withdrawal Top-Up
                </h3>
                <p className={`${globalStyle.body2} text-gray-400`}>
                  Users who have earned Level-Up (tier) bonuses get an{" "}
                  <strong className="text-white">
                    extra withdrawal allowance
                  </strong>{" "}
                  on top of their activity-based limit. Set what percentage of
                  their{" "}
                  <strong className="text-white">
                    all-time total level-up bonus earnings
                  </strong>{" "}
                  they can withdraw as an additional daily allowance.
                </p>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-800/40 rounded-lg p-4 mb-5">
                <h4 className="text-sm font-medium text-yellow-300 mb-2">
                  How it works
                </h4>
                <ul className="text-sm text-yellow-200 space-y-1">
                  <li>
                    · Sum all approved Level-Up bonuses the user has ever
                    received
                  </li>
                  <li>
                    · Multiply by this percentage to get an extra daily
                    allowance
                  </li>
                  <li>
                    · This is{" "}
                    <span className="text-white font-medium">added on top</span>{" "}
                    of the tier-based limit, not instead of it
                  </li>
                  {withdrawalSettings.level_bonus_pct > 0 && (
                    <li className="text-white font-medium mt-2 pt-2 border-t border-yellow-800/40">
                      Example: user with 500 XRP in all-time level bonuses → +
                      {roundToClean(
                        (500 * withdrawalSettings.level_bonus_pct) / 100,
                      ).toLocaleString()}{" "}
                      XRP/day extra
                    </li>
                  )}
                </ul>
              </div>

              <div className={globalStyle.formGroup}>
                <label className={globalStyle.label}>
                  % of all-time level-up bonuses added to daily limit
                </label>
                <div className="flex items-center gap-3 max-w-xs">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={withdrawalSettings.level_bonus_pct}
                    onChange={(e) =>
                      setWithdrawalSettings((p) => ({
                        ...p,
                        level_bonus_pct: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className={globalStyle.input}
                    placeholder="40"
                  />
                  <span className="text-white font-bold text-lg shrink-0">
                    %
                  </span>
                </div>
                <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
                  Set to 0 to disable the level-up bonus top-up entirely.
                </p>
              </div>
            </Card>

            {/* Live Calculator */}
            <Card padding="lg">
              <div className="flex items-center gap-2 mb-1">
                <CalculatorIcon className="w-5 h-5 text-primary" />
                <h3 className={globalStyle.h5}>Limit Calculator</h3>
              </div>
              <p className={`${globalStyle.body2} text-gray-400 mb-5`}>
                Enter any 7-day earnings total to preview the withdrawal limit a
                user with those earnings would receive under the{" "}
                <strong className="text-gray-300">current settings</strong>.
                Mirrors backend logic exactly.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div className={globalStyle.formGroup}>
                  <label className={`${globalStyle.label} text-xs`}>
                    7-day earnings (XRP)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="10"
                      value={calcEarnings}
                      onChange={(e) => setCalcEarnings(e.target.value)}
                      className={`${globalStyle.input} pr-14`}
                      placeholder="e.g. 350"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                      XRP
                    </span>
                  </div>
                </div>
                <div className={globalStyle.formGroup}>
                  <label className={`${globalStyle.label} text-xs`}>
                    All-time level-up bonuses earned (XRP)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="10"
                      value={calcLvlBonus}
                      onChange={(e) => setCalcLvlBonus(e.target.value)}
                      className={`${globalStyle.input} pr-14`}
                      placeholder="e.g. 500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                      XRP
                    </span>
                  </div>
                </div>
              </div>

              {calcResult && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                  <div className="sm:col-span-2 bg-gray-900 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">
                      Total daily limit
                    </p>
                    <p className="text-2xl font-bold text-white">
                      {calcResult.limit.toLocaleString()}
                      <span className="text-sm font-normal text-gray-400 ml-2">
                        XRP / day
                      </span>
                    </p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="text-xs text-gray-400">
                        Base:{" "}
                        <span className="text-white">
                          {calcResult.baseLimit.toLocaleString()} XRP
                        </span>
                      </span>
                      {calcResult.bonusTopUp > 0 && (
                        <span className="text-xs text-yellow-400">
                          + Bonus top-up:{" "}
                          {calcResult.bonusTopUp.toLocaleString()} XRP
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-4 flex flex-col justify-center items-start gap-2">
                    <p className="text-xs text-gray-500">Tier assigned</p>
                    <TierBadge tier={calcResult.tier} />
                    <p className="text-xs text-gray-500">
                      {calcResult.pct}% applied
                    </p>
                  </div>
                </div>
              )}

              {/* Quick test values */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Quick test values:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: "0 XRP", val: 0 },
                    { label: "50 XRP", val: 50 },
                    { label: "100 XRP", val: 100 },
                    { label: "250 XRP", val: 250 },
                    { label: "500 XRP", val: 500 },
                    { label: "1 000", val: 1000 },
                    { label: "5 000", val: 5000 },
                  ].map(({ label, val }) => (
                    <button
                      key={val}
                      onClick={() => setCalcEarnings(String(val))}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${parseFloat(calcEarnings) === val ? "border-primary bg-primary/10 text-primary" : "border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            {/* Save */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveWithdrawalSettings}
                loading={withdrawalSettingsLoading}
                disabled={
                  withdrawalSettings.allowed_days.length === 0 ||
                  withdrawalSettings.tier1_threshold <=
                    withdrawalSettings.tier2_threshold ||
                  withdrawalSettings.tier1_pct <= 0 ||
                  withdrawalSettings.tier2_pct <= 0 ||
                  withdrawalSettings.tier3_pct <= 0
                }
              >
                Save Withdrawal Settings
              </Button>
            </div>
          </div>
        )}

        {/* ── Dashboard — Broadcast Message ───────────────────────────────── */}
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            <Card padding="lg">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className={globalStyle.h5}>Broadcast Message</h3>
                  <p className={`${globalStyle.body2} text-gray-400 mt-1`}>
                    Display a scrolling announcement on every user's dashboard.
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={`text-sm font-medium ${broadcast.is_active ? "text-green-400" : "text-gray-500"}`}
                  >
                    {broadcast.is_active ? "LIVE" : "Hidden"}
                  </span>
                  <button
                    onClick={() =>
                      setBroadcast((b) => ({ ...b, is_active: !b.is_active }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${broadcast.is_active ? "bg-green-600" : "bg-gray-700"}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${broadcast.is_active ? "translate-x-6" : "translate-x-1"}`}
                    />
                  </button>
                </div>
              </div>

              {broadcast.message.trim() && (
                <div
                  className={`mb-6 rounded-lg px-4 py-2 overflow-hidden ${{ info: "bg-blue-900 border border-blue-700", warning: "bg-yellow-900 border border-yellow-700", success: "bg-green-900 border border-green-700", danger: "bg-red-900 border border-red-700" }[broadcast.type]}`}
                >
                  <p className="text-xs text-gray-400 mb-1">Preview</p>
                  <div
                    className="overflow-hidden relative"
                    style={{ height: 24 }}
                  >
                    <span
                      className={`absolute whitespace-nowrap text-sm font-medium ${{ info: "text-blue-200", warning: "text-yellow-200", success: "text-green-200", danger: "text-red-200" }[broadcast.type]}`}
                      style={{
                        animation: `preview-marquee ${Math.max(4, Math.round((broadcast.message.length * 7 + 400) / broadcast.speed))}s linear infinite`,
                      }}
                    >
                      📢 &nbsp; {broadcast.message}
                    </span>
                  </div>
                  <style>{`@keyframes preview-marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }`}</style>
                </div>
              )}

              <div className={globalStyle.formGroup}>
                <label className={globalStyle.label}>Message Text *</label>
                <textarea
                  value={broadcast.message}
                  onChange={(e) =>
                    setBroadcast((b) => ({ ...b, message: e.target.value }))
                  }
                  className={`${globalStyle.input} min-h-[90px] resize-none`}
                  placeholder="e.g. 🎉 Platform maintenance scheduled for Sunday 2:00 AM UTC."
                  maxLength={500}
                />
                <div className="flex justify-between mt-1">
                  <p className={`${globalStyle.caption} text-gray-500`}>
                    Max 500 characters
                  </p>
                  <p
                    className={`${globalStyle.caption} ${broadcast.message.length > 450 ? "text-yellow-400" : "text-gray-500"}`}
                  >
                    {broadcast.message.length}/500
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={globalStyle.formGroup}>
                  <label className={globalStyle.label}>Banner Style</label>
                  <select
                    value={broadcast.type}
                    onChange={(e) =>
                      setBroadcast((b) => ({
                        ...b,
                        type: e.target.value as any,
                      }))
                    }
                    className={globalStyle.input}
                  >
                    <option value="info">ℹ️ Info (blue)</option>
                    <option value="warning">⚠️ Warning (yellow)</option>
                    <option value="success">✅ Success (green)</option>
                    <option value="danger">🚨 Danger (red)</option>
                  </select>
                </div>
                <div className={globalStyle.formGroup}>
                  <label className={globalStyle.label}>
                    Scroll Speed —{" "}
                    <span className="text-primary">{broadcast.speed} px/s</span>
                  </label>
                  <input
                    type="range"
                    min={10}
                    max={200}
                    step={5}
                    value={broadcast.speed}
                    onChange={(e) =>
                      setBroadcast((b) => ({
                        ...b,
                        speed: Number(e.target.value),
                      }))
                    }
                    className="w-full accent-primary"
                  />
                  <div className="flex justify-between mt-1">
                    <span className={`${globalStyle.caption} text-gray-500`}>
                      Slow
                    </span>
                    <span className={`${globalStyle.caption} text-gray-500`}>
                      Fast
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mb-4">
                <div
                  className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${broadcast.is_active ? "bg-green-900/30 border border-green-800/40 text-green-300" : "bg-gray-800 border border-gray-700 text-gray-500"}`}
                >
                  {broadcast.is_active ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4" /> Showing on all
                      dashboards
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="w-4 h-4" /> Hidden from users
                    </>
                  )}
                </div>
              </div>

              <Button
                onClick={handleSaveBroadcast}
                loading={broadcastLoading}
                disabled={broadcast.is_active && !broadcast.message.trim()}
                fullWidth
              >
                <MegaphoneIcon className="w-4 h-4 mr-2" />
                {broadcast.is_active ? "Save & Go Live" : "Save Settings"}
              </Button>
            </Card>
          </div>
        )}
      </main>

      <BottomNav />

      {/* ── Add XRP Address Modal ──────────────────────────────────────────── */}
      <Modal
        isOpen={showAddAddress}
        onClose={() => setShowAddAddress(false)}
        title="Add XRP Address"
        size="md"
      >
        <div className="space-y-6">
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>XRP Address *</label>
            <input
              type="text"
              value={newAddress.address}
              onChange={(e) =>
                setNewAddress({ ...newAddress, address: e.target.value })
              }
              className={globalStyle.input}
              placeholder="rXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
            />
            {newAddress.address && !validateXrpAddress(newAddress.address) && (
              <p className={`${globalStyle.formError} mt-2`}>
                Invalid XRP address format
              </p>
            )}
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Label (Optional)</label>
            <input
              type="text"
              value={newAddress.label}
              onChange={(e) =>
                setNewAddress({ ...newAddress, label: e.target.value })
              }
              className={globalStyle.input}
              placeholder="Main Deposit Address"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>Destination Tag</label>
              <input
                type="text"
                value={newAddress.destination_tag}
                onChange={(e) =>
                  setNewAddress({
                    ...newAddress,
                    destination_tag: e.target.value,
                  })
                }
                className={globalStyle.input}
                placeholder="123456"
              />
            </div>
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>Memo Type</label>
              <select
                value={newAddress.memo_type}
                onChange={(e) =>
                  setNewAddress({ ...newAddress, memo_type: e.target.value })
                }
                className={globalStyle.input}
              >
                <option value="">Select Type</option>
                <option value="MEMO">MEMO</option>
                <option value="MEMO_HEX">MEMO_HEX</option>
                <option value="MEMO_ID">MEMO_ID</option>
              </select>
            </div>
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>
              QR Code Image (Optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setNewAddress({
                  ...newAddress,
                  qr_code: e.target.files?.[0] || null,
                })
              }
              className={globalStyle.input}
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={newAddress.is_active}
              onChange={(e) =>
                setNewAddress({ ...newAddress, is_active: e.target.checked })
              }
              className="w-4 h-4 text-primary border-gray-700 rounded bg-gray-800"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-white">
              Set as active deposit address
            </label>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="tertiary"
              onClick={() => setShowAddAddress(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddAddress}
              loading={loading}
              disabled={
                !newAddress.address.trim() ||
                !validateXrpAddress(newAddress.address)
              }
              fullWidth
            >
              Add Address
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Edit XRP Address Modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={!!showEditAddress}
        onClose={() => setShowEditAddress(null)}
        title="Edit XRP Address"
        size="md"
      >
        {showEditAddress && (
          <div className="space-y-6">
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-sm font-medium text-white">XRP Address</p>
              <code className="text-sm font-mono mt-1 text-white">
                {showEditAddress.address}
              </code>
            </div>
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>Label</label>
              <input
                type="text"
                value={showEditAddress.label || ""}
                onChange={(e) =>
                  setShowEditAddress({
                    ...showEditAddress,
                    label: e.target.value,
                  })
                }
                className={globalStyle.input}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className={globalStyle.formGroup}>
                <label className={globalStyle.label}>Destination Tag</label>
                <input
                  type="text"
                  value={showEditAddress.destination_tag || ""}
                  onChange={(e) =>
                    setShowEditAddress({
                      ...showEditAddress,
                      destination_tag: e.target.value,
                    })
                  }
                  className={globalStyle.input}
                />
              </div>
              <div className={globalStyle.formGroup}>
                <label className={globalStyle.label}>Memo Type</label>
                <select
                  value={showEditAddress.memo_type || ""}
                  onChange={(e) =>
                    setShowEditAddress({
                      ...showEditAddress,
                      memo_type: e.target.value,
                    })
                  }
                  className={globalStyle.input}
                >
                  <option value="">Select Type</option>
                  <option value="MEMO">MEMO</option>
                  <option value="MEMO_HEX">MEMO_HEX</option>
                  <option value="MEMO_ID">MEMO_ID</option>
                </select>
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="edit_is_active"
                checked={showEditAddress.is_active}
                onChange={(e) =>
                  setShowEditAddress({
                    ...showEditAddress,
                    is_active: e.target.checked,
                  })
                }
                className="w-4 h-4 text-primary border-gray-700 rounded bg-gray-800"
              />
              <label
                htmlFor="edit_is_active"
                className="ml-2 text-sm text-white"
              >
                Set as active deposit address
              </label>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="tertiary"
                onClick={() => setShowEditAddress(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateAddress} loading={loading} fullWidth>
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete XRP Address Modal ───────────────────────────────────────── */}
      <Modal
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        title="Delete XRP Address"
        size="sm"
      >
        {showDeleteConfirm && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-900 flex items-center justify-center mx-auto mb-4">
                <TrashIcon className="w-8 h-8 text-red-300" />
              </div>
              <h3 className={globalStyle.h5}>Delete Address?</h3>
              <div className="mt-4 bg-gray-800 p-3 rounded-lg">
                <code className="text-sm font-mono text-white">
                  {showDeleteConfirm.address}
                </code>
              </div>
            </div>
            {showDeleteConfirm.is_active && (
              <div className="bg-red-900 border border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-200">
                  This is the active deposit address. Deleting it will disable
                  deposits until another is activated.
                </p>
              </div>
            )}
            <div className="flex space-x-3">
              <Button
                variant="tertiary"
                onClick={() => setShowDeleteConfirm(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAddress}
                loading={loading}
                fullWidth
              >
                Delete Address
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Create Airdrop Modal ───────────────────────────────────────────── */}
      <Modal
        isOpen={showAddAirdrop}
        onClose={() => setShowAddAirdrop(false)}
        title="Create New Airdrop"
        size="md"
      >
        <div className="space-y-6">
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Airdrop Name *</label>
            <input
              type="text"
              value={newAirdrop.name}
              onChange={(e) =>
                setNewAirdrop({ ...newAirdrop, name: e.target.value })
              }
              className={globalStyle.input}
              placeholder="Special Reward Airdrop"
            />
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Description</label>
            <textarea
              value={newAirdrop.description}
              onChange={(e) =>
                setNewAirdrop({ ...newAirdrop, description: e.target.value })
              }
              className={globalStyle.input}
              rows={3}
            />
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Amount per User (XRP) *</label>
            <input
              type="number"
              step="0.000001"
              min="0.1"
              value={newAirdrop.amount}
              onChange={(e) =>
                setNewAirdrop({ ...newAirdrop, amount: e.target.value })
              }
              className={globalStyle.input}
            />
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Distribution Date *</label>
            <input
              type="datetime-local"
              value={newAirdrop.distribution_date}
              onChange={(e) =>
                setNewAirdrop({
                  ...newAirdrop,
                  distribution_date: e.target.value,
                })
              }
              className={globalStyle.input}
              min={new Date().toISOString().slice(0, 16)}
            />
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>
              Criteria (JSON — optional)
            </label>
            <textarea
              value={newAirdrop.criteria}
              onChange={(e) =>
                setNewAirdrop({ ...newAirdrop, criteria: e.target.value })
              }
              className={globalStyle.input}
              rows={3}
              placeholder='{"min_balance": 100}'
            />
          </div>
          <div className="flex space-x-3">
            <Button
              variant="tertiary"
              onClick={() => setShowAddAirdrop(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddAirdrop}
              loading={loading}
              disabled={!newAirdrop.name.trim() || !newAirdrop.amount.trim()}
              fullWidth
            >
              Create Airdrop
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Edit Airdrop Modal ─────────────────────────────────────────────── */}
      <Modal
        isOpen={!!showEditAirdrop}
        onClose={() => setShowEditAirdrop(null)}
        title="Edit Airdrop"
        size="md"
      >
        {showEditAirdrop && (
          <div className="space-y-6">
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>Airdrop Name *</label>
              <input
                type="text"
                value={showEditAirdrop.name || ""}
                onChange={(e) =>
                  setShowEditAirdrop({
                    ...showEditAirdrop,
                    name: e.target.value,
                  })
                }
                className={globalStyle.input}
              />
            </div>
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>Description</label>
              <textarea
                value={showEditAirdrop.description || ""}
                onChange={(e) =>
                  setShowEditAirdrop({
                    ...showEditAirdrop,
                    description: e.target.value,
                  })
                }
                className={globalStyle.input}
                rows={3}
              />
            </div>
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>
                Amount per User (XRP) *
              </label>
              <input
                type="number"
                step="0.000001"
                min="0.1"
                value={showEditAirdrop.amount || ""}
                onChange={(e) =>
                  setShowEditAirdrop({
                    ...showEditAirdrop,
                    amount: e.target.value,
                  })
                }
                className={globalStyle.input}
              />
            </div>
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>Distribution Date *</label>
              <input
                type="datetime-local"
                value={showEditAirdrop.distribution_date || ""}
                onChange={(e) =>
                  setShowEditAirdrop({
                    ...showEditAirdrop,
                    distribution_date: e.target.value,
                  })
                }
                className={globalStyle.input}
              />
            </div>
            <div className="flex space-x-3">
              <Button
                variant="tertiary"
                onClick={() => setShowEditAirdrop(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditAirdrop}
                loading={loading}
                disabled={!showEditAirdrop.name?.trim()}
                fullWidth
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete Airdrop Modal ───────────────────────────────────────────── */}
      <Modal
        isOpen={!!showDeleteAirdrop}
        onClose={() => setShowDeleteAirdrop(null)}
        title="Delete Airdrop"
        size="sm"
      >
        {showDeleteAirdrop && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-900 flex items-center justify-center mx-auto mb-4">
                <TrashIcon className="w-8 h-8 text-red-300" />
              </div>
              <h3 className={globalStyle.h5}>Delete Airdrop?</h3>
              <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                This cannot be undone.
              </p>
              <div className="mt-4 bg-gray-800 p-3 rounded-lg">
                <p className="text-sm font-medium text-white">
                  {showDeleteAirdrop.name}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="tertiary"
                onClick={() => setShowDeleteAirdrop(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAirdrop}
                loading={loading}
                fullWidth
              >
                Delete Airdrop
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Distribute Airdrop Modal ───────────────────────────────────────── */}
      <Modal
        isOpen={!!showDistributeAirdrop}
        onClose={() => setShowDistributeAirdrop(null)}
        title="Distribute Airdrop Now"
        size="sm"
      >
        {showDistributeAirdrop && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-900 flex items-center justify-center mx-auto mb-4">
                <GiftIcon className="w-8 h-8 text-blue-300" />
              </div>
              <h3 className={globalStyle.h5}>Distribute Now?</h3>
              <div className="mt-4 bg-gray-800 p-3 rounded-lg">
                <p className="text-sm font-medium text-white">
                  {showDistributeAirdrop.name}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {formatXRPAmount(showDistributeAirdrop.amount)} per user
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="tertiary"
                onClick={() => setShowDistributeAirdrop(null)}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleDistributeAirdrop}
                loading={loading}
                fullWidth
              >
                Distribute Now
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminSettings;
